import { Selector } from '@/state/useContainerWizzardSteps'
import query from './APIServicesV2'

interface Containers {
  id: number
  status: string
  createdAt: string
  updatedAt: string
  operationNumber: number
  isReturned: false
  totalItems: number
}

interface ContainerDetailsResponse {
  id: number
  name: string
  status: string
  operationNumber: number
  createdAt: string
  updatedAt: string
  isReturned: boolean
  observations: string
  trackingNumber: number
  levanteNumber: number
  grossWeight: number
  netWeight: number
  totalItems: number
  isRefrigered: boolean
  temperatureRange: string
  code: string
  sealNumber: string
  categories: Selector[]
  ownerCompany: Selector | null
  managerCompany: Selector | null
  countryFrom: Selector | null
  origin: Selector | null
  destiny: Selector | null
}

export const getContainers = async () => {
  const response = await query.get('/containers')
  return response?.data.items as Containers[]
}

export const getContainerDetails = async (id: number) => {
  const response = await query.get(`/containers/${id}/details`)
  return response?.data as ContainerDetailsResponse
}

export const createContainer = async (data: any, version: 'v1' | 'v2' = 'v2') => {
  // Mapear costs si existen en data.listCosts
  const costs = Array.isArray(data.listCosts)
    ? data.listCosts.map((item: any) => ({
        costAmount: item.costAmount ?? null,
        observations: item.observations ?? null,
        type: item.type ?? null,
        fixedCostCategoryId: item.fixedCostCategoryId ?? null,
      }))
    : [];

  const containerData: Record<string, any> = {
    name: data.name,
    observations: data.observations,
    code: data.code,
    sealNumber: data.sealNumber,
    isRefrigered: data.isRefrigered,
    temperatureRange: data.temperatureRange,
    categories: data.categories,
    costs: costs,
    netWeight: data.netWeight,
    isPalleted: data.isPalleted,
    numberOfPallets: data.numberOfPallets,
    trackingNumber: data.trackingNumber,
    ownerCompanyId: data.ownerCompanyId,
    importerCompanyId: data.importerCompanyId,
    transporterCompanyId: data.transporterCompanyId,
    storageCompanyId: data.storageCompanyId,
    managerCompanyId: data.managerCompanyId,
    transportedBy: data.transportedBy,
    countryFromId: data.countryFromId,
    originId: data.originId,
    destinyId: data.destinyId,
  };

  // Filter out null, undefined, and false values for specific boolean properties
  Object.keys(containerData).forEach(key => {
    if (containerData[key] === null || containerData[key] === undefined ||
        (key === 'isRefrigered' && containerData[key] === false) ||
        (key === 'isPalleted' && containerData[key] === false)) {
      delete containerData[key];
    }
  });







  // Si version es v2, construye la URL base manualmente usando la variable de entorno V2
  if (version === 'v2') {
    const apiHost = process.env.REACT_APP_API_HOST || ''
    const apiV2 = process.env.REACT_APP_VERSION_API_V2 || '/v2'
    const url = `${apiHost}${apiV2}/containers`
    const axios = (await import('axios')).default
    // Importa el store global correctamente
    const { store } = await import('../store/root')
    const token = store.getState().session?.key?.token || ''
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-App-Origin': 'Tecopos-Admin',
    }
    if (token) headers['Authorization'] = `Bearer ${token}`
    return (await axios.post(url, containerData, { headers })).data
  }
  // Por defecto usa el sistema de APIServices (que concatena la versión principal)
  const response = await query.post('/containers', containerData)
  return response?.data
}

// Container costs API functions
export const getContainerCosts = async (params: { id: number }) => {
  const response = await query.get(`/containers/${params.id}/costs`)
  return response.data
}

export const postContainerCosts = async ({
  id,
  params,
}: {
  id: number
  params: any
}) => await query.post(`/containers/${id}/costs`, params)

export const patchContainerCosts = async ({
  id,
  params,
}: {
  id: number
  params: any
}) => await query.patch(`/containers/${id}/costs/${id}`, params)

export const deleteContainerCosts = async (id: number) => 
  await query.deleteAPI(`/containers/costs/${id}`)

// Container products API functions
export const getContainerProducts = async (params: { id: number }) => {
  const response = await query.get(`/containers/${params.id}/products`)
  return response.data
}

export const postContainerProducts = async ({
  id,
  params,
}: {
  id: number
  params: any
}) => await query.post(`/containers/${id}/products`, params)

export const patchContainerProducts = async ({
  id,
  params,
}: {
  id: number
  params: any
}) => await query.patch(`/containers/${id}/products/${id}`, params)

export const deleteContainerProducts = async (id: number) => 
  await query.deleteAPI(`/containers/products/${id}`)

// Container documents API functions
export const getContainerDocuments = async (params: { id: number }) => {
  const response = await query.get(`/containers/${params.id}/documents`)
  return response.data
}

export const postContainerDocuments = async ({
  id,
  params,
}: {
  id: number
  params: any
}) => {
  const payload = { ...params, containerId: id }
  return await query.post(`/containers/documents`, payload)
}

export const patchContainerDocuments = async ({
  id,
  params,
}: {
  id: number
  params: any
}) => await query.patch(`/containers/documents/${id}`, params)

export const deleteContainerDocuments = async (id: number) => 
  await query.deleteAPI(`/containers/documents/${id}`)
